/**
 * useHarmonic
 *
 * Thin client over /.netlify/functions/harmonic, which is a transparent proxy
 * to https://api.harmonic.ai. Methods mirror Harmonic's REST surface and
 * return raw responses — the UI consumes Harmonic's field names directly.
 *
 * Status semantics from Harmonic:
 *   200  data
 *   201  data + background re-enrichment (treated as 200)
 *   404  not found / enrichment triggered — surfaced as { found: false, status: "enriching" }
 *
 * Caching: in-memory, page-session lifetime. GETs and enrichment POSTs are
 * cached by full URL+body so repeat lookups don't burn quota. 404s are not
 * cached so retries actually retry.
 */
import type { ApolloClient } from "@apollo/client/core"
import { HttpError, httpErrorMessage, type RequestOptions } from "@/common/fetchclient"
import { netlifyFunctionClient } from "@/common/functions"
import {
  HARMONIC_KIND_RECORD_TYPE,
  HARMONIC_KIND_SOURCE_TYPE,
  normalizeDomain,
} from "@/common/harmonic"
import { buildHarmonicContext } from "@/common/harmonicContext"
import { fetchRecordsForSources, findRecordsByDomain } from "@/common/hasura/records"
import type { ComposableDeps } from "@/composables/types"
import { getAuger } from "@/init/auger"

const netlifyClient = netlifyFunctionClient({
  baseURL: window.location.origin + "/.netlify/functions/harmonic",
})

type Found<T> = { found: true; data: T }
type NotFound = { found: false; status: "enriching" | "not_found" }
export type HarmonicResult<T> = Found<T> | NotFound

const cache = new Map<string, Promise<unknown>>()

function qs(params?: Record<string, unknown>): string {
  if (!params) return ""
  const entries: [string, string][] = []
  for (const [k, v] of Object.entries(params)) {
    if (v == null) continue
    if (Array.isArray(v)) v.forEach((item) => entries.push([k, String(item)]))
    else entries.push([k, String(v)])
  }
  return entries.length ? "?" + new URLSearchParams(entries).toString() : ""
}

async function request<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  params?: Record<string, unknown>,
  body?: unknown
): Promise<T> {
  const url = path + qs(params)
  const cacheKey = `${method}:${url}:${body ? JSON.stringify(body) : ""}`

  if (method === "GET" && cache.has(cacheKey)) return cache.get(cacheKey) as Promise<T>
  if (method === "POST" && cache.has(cacheKey)) return cache.get(cacheKey) as Promise<T>

  const opts: RequestOptions = { method }
  if (body !== undefined) opts.body = JSON.stringify(body)

  // A Harmonic success always carries a JSON body.
  const promise = netlifyClient.request<T>(url, opts).then(({ data }) => data as T)

  if (method === "GET" || method === "POST") {
    cache.set(
      cacheKey,
      promise.catch((err: unknown) => {
        cache.delete(cacheKey)
        throw err
      })
    )
  }
  return promise
}

// Enrichment POSTs use HarmonicResult so callers can distinguish 404-enriching
// from network errors. 404s aren't cached — retries should hit the API.
async function enrichmentRequest<T>(
  path: string,
  params: Record<string, unknown>
): Promise<HarmonicResult<T>> {
  const url = path + qs(params)
  const cacheKey = `enrich:${url}`
  if (cache.has(cacheKey)) return cache.get(cacheKey) as Promise<HarmonicResult<T>>

  const promise = netlifyClient
    .request<T>(url, { method: "POST" })
    .then(({ data }): HarmonicResult<T> => ({ found: true, data: data as T }))
    .catch((err: unknown): HarmonicResult<T> => {
      if (err instanceof HttpError && err.status === 404) {
        const body = httpErrorMessage(err) || ""
        const status = /enrich/i.test(body) ? "enriching" : "not_found"
        cache.delete(cacheKey)
        return { found: false, status }
      }
      cache.delete(cacheKey)
      throw err
    })

  cache.set(cacheKey, promise)
  return promise
}

// ============================================
// Companies
// ============================================
export interface HarmonicTag {
  type?: string
  display_value?: string
}

export interface HarmonicTractionMetric {
  latest_metrics_value?: number
  "30d_ago"?: { percent_change?: number | string }
}

export interface HarmonicFundingRoundInvestor {
  association_urn?: string
  investor_name?: string
  is_lead?: boolean
}

export interface HarmonicFundingRound {
  entity_urn?: string
  source_url?: string
  funding_round_type?: string
  announcement_date?: string
  funding_amount?: number
  post_money_valuation?: number
  investors?: HarmonicFundingRoundInvestor[]
}

export interface HarmonicFunding {
  investors?: Array<{ name?: string; entity_urn?: string }>
  funding_total?: number
  last_funding_type?: string
  last_funding_total?: number
  last_funding_at?: string
  valuation?: number
  num_funding_rounds?: number
}

export interface HarmonicHighlight {
  category?: string
  text?: string
}

export interface HarmonicRelatedCompanies {
  acquisitions?: unknown[]
  acquired_by?: { company_urn?: string } | null
  subsidiaries?: unknown[]
  subsidiary_of?: { company_urn?: string } | null
}

export interface HarmonicCompany {
  id?: number
  entity_urn?: string
  name?: string
  legal_name?: string
  description?: string
  short_description?: string
  external_description?: string
  website?: { url?: string; domain?: string } | string
  website_domain_aliases?: string[]
  socials?: Record<string, { url?: string; follower_count?: number }>
  logo_url?: string
  headcount?: number
  stage?: string
  customer_type?: string
  founding_date?: { date?: string }
  location?: {
    /** The operating HQ; `address_formatted` can be a registered-agent address. */
    location?: string
    address_formatted?: string
    city?: string
    state?: string
    country?: string
  }
  tags?: HarmonicTag[]
  tags_v2?: HarmonicTag[]
  funding?: HarmonicFunding
  funding_rounds?: HarmonicFundingRound[]
  // Keyed by metric name (e.g. "headcount", "headcount_engineering", "web_traffic").
  traction_metrics?: Record<string, HarmonicTractionMetric | undefined>
  highlights?: HarmonicHighlight[]
  employee_highlights?: HarmonicHighlight[]
  related_companies?: HarmonicRelatedCompanies
  [key: string]: unknown
}

export interface HarmonicPerson {
  id?: number
  entity_urn?: string
  full_name?: string
  first_name?: string
  last_name?: string
  linkedin_headline?: string
  profile_picture_url?: string
  experience?: Array<{
    is_current_position?: boolean
    title?: string
    company_name?: string
    [key: string]: unknown
  }>
  socials?: Record<string, { url?: string }>
  contact?: { emails?: string[]; phone_numbers?: string[] }
  location?: { address_formatted?: string; city?: string; state?: string; country?: string }
  [key: string]: unknown
}

// ============================================
// Public API
// ============================================
export function useHarmonic() {
  return {
    // Companies
    getCompany: (idOrUrn: string | number) =>
      request<HarmonicCompany>("GET", `/companies/${encodeURIComponent(String(idOrUrn))}`),
    getCompanies: (params: { ids?: number[]; urns?: string[]; include_fields?: string[] }) =>
      request<HarmonicCompany[]>("GET", "/companies", params),
    enrichCompany: (params: {
      website_domain?: string
      website_url?: string
      linkedin_url?: string
      crunchbase_url?: string
      pitchbook_url?: string
    }) => enrichmentRequest<HarmonicCompany>("/companies", params),
    getCompanyEmployees: (idOrUrn: string | number, params?: { page?: number; size?: number }) =>
      request(
        "GET",
        `/companies/${encodeURIComponent(String(idOrUrn))}/employees`,
        params as Record<string, unknown>
      ),

    // Persons
    getPerson: (idOrUrn: string | number) =>
      request<HarmonicPerson>("GET", `/persons/${encodeURIComponent(String(idOrUrn))}`),
    getPersons: (params: { ids?: number[]; urns?: string[]; company_context_urns?: string[] }) =>
      request<HarmonicPerson[]>("GET", "/persons", params),
    enrichPerson: (params: { email?: string; linkedin_url?: string }) =>
      enrichmentRequest<HarmonicPerson>("/persons", params),

    // Search
    typeahead: (params: {
      query: string
      search_type?: "COMPANY" | "PERSON"
      page?: number
      size?: number
    }) =>
      request<{ results?: Array<{ entity_urn?: string }> }>(
        "GET",
        "/search/typeahead",
        params as Record<string, unknown>
      ),
    searchCompaniesByKeywords: (body: unknown, params?: { page?: number; size?: number }) =>
      request("POST", "/search/companies_by_keywords", params as Record<string, unknown>, body),
    similarCompanies: (idOrUrn: string | number) =>
      request("GET", `/search/similar_companies/${encodeURIComponent(String(idOrUrn))}`),
    searchAgent: (params: { query: string }) =>
      request("GET", "/search/search_agent", params as Record<string, unknown>),

    // Watchlists — companies
    listCompanyWatchlists: (params?: Record<string, unknown>) =>
      request("GET", "/watchlists/companies", params),
    getCompanyWatchlist: (idOrUrn: string | number, params?: Record<string, unknown>) =>
      request("GET", `/watchlists/companies/${encodeURIComponent(String(idOrUrn))}`, params),
    addCompaniesToWatchlist: (
      idOrUrn: string | number,
      body: { ids?: number[]; urns?: string[] }
    ) =>
      request(
        "POST",
        `/watchlists/companies/${encodeURIComponent(String(idOrUrn))}:addCompanies`,
        undefined,
        body
      ),

    // Watchlists — people
    listPersonWatchlists: (params?: Record<string, unknown>) =>
      request("GET", "/watchlists/people", params),
    getPersonWatchlist: (idOrUrn: string | number, params?: Record<string, unknown>) =>
      request("GET", `/watchlists/people/${encodeURIComponent(String(idOrUrn))}`, params),

    // Saved searches
    listSavedSearches: () => request("GET", "/saved_searches"),
    getSavedSearchResults: (
      idOrUrn: string | number,
      params?: { cursor?: string; page?: number; size?: number }
    ) =>
      request(
        "GET",
        `/saved_searches:results/${encodeURIComponent(String(idOrUrn))}`,
        params as Record<string, unknown>
      ),
    getSavedSearchNetNew: (idOrUrn: string | number) =>
      request("GET", `/saved_searches/${encodeURIComponent(String(idOrUrn))}/net_new_results`),

    // Enrichment status
    getEnrichmentStatus: () => request("GET", "/enrichment_status"),
  }
}

// ============================================
// Standalone helpers (for non-component callers)
// ============================================
const harmonic = useHarmonic()
export const enrichCompany = harmonic.enrichCompany

// ============================================
// Person record enrichment — single entry point
// ============================================

export interface EnrichPersonRecordOptions {
  emails?: string[]
  record?: {
    id: number
    name?: string
    description?: string | null
    data?: Record<string, unknown> | null
  } | null
  recordName?: string
  updateFields?: boolean
  addContacts?: boolean
  addPhoneContacts?: boolean
  addSocialContacts?: boolean
  addCityTag?: boolean
}

export interface EnrichPersonRecordResult {
  status: "found" | "not_found" | "enriching" | "no_emails"
  recordId?: number
  urn?: string
  data?: HarmonicPerson
}

/**
 * One entry point for Harmonic person enrichment.
 *
 * Iterates the provided emails until Harmonic returns a hit, then:
 *   - creates the person record if `record` is null (using Harmonic full_name),
 *     seeding the matched email so a brand-new record isn't empty
 *   - links a `harmonic_person` source on the record with context (idempotent)
 *
 * Field/contact/title/location/tag writes are NOT done here. Linking the
 * `harmonic_person` source fires a PG trigger that enqueues server-side
 * enrichment; the worker fills the rest within ~5 min. The legacy field-writing
 * flags on `EnrichPersonRecordOptions` are retained for caller compatibility but
 * are now no-ops.
 */
export async function enrichPersonRecord(
  options: EnrichPersonRecordOptions,
  { client }: Pick<ComposableDeps, "client">
): Promise<EnrichPersonRecordResult> {
  const { emails = [], record = null, recordName } = options

  if (!emails.length) return { status: "no_emails" }

  let hit: HarmonicPerson | null = null
  let matchedEmail: string | null = null
  let lastStatus: "not_found" | "enriching" = "not_found"

  for (const email of emails) {
    const result: HarmonicResult<HarmonicPerson> = await harmonic.enrichPerson({ email })
    if (result.found === true) {
      hit = result.data
      matchedEmail = email
      break
    }
    lastStatus = result.status
  }

  if (!hit) return { status: lastStatus }

  const urn = hit.entity_urn
  if (!urn) return { status: "not_found" }

  let target = record
  if (!target) {
    const created = await getAuger(client).records.create({
      type: "person",
      name: hit.full_name || recordName || matchedEmail || "",
    })
    target = {
      id: created.id,
      name: created.name,
      description: created.description,
      data: created.data,
    }
    if (matchedEmail) {
      const auger = getAuger(client)
      await auger
        .record(target.id)
        .contacts.add("contacts", { contactType: "email", value: matchedEmail, isPrimary: true })
    }
  }

  // Idempotent source write — skip if a harmonic_person source for this URN
  // already exists on the record. Linking the source is the entire trigger for
  // server-side enrichment, so a failed link means nothing will enrich. Rethrow
  // so the caller surfaces the failure rather than falsely reporting success.
  // (All callers wrap this in try/catch or .catch — see the caller audit.)
  try {
    const auger = getAuger(client)
    const existingSources = await auger.sources.list(target.id)
    const alreadyLinked = existingSources.some(
      (s) => s.source_type === "harmonic_person" && s.source_id === urn
    )
    if (!alreadyLinked) {
      await linkSource(target.id, "harmonic_person", urn, client, buildHarmonicContext(hit))
    }
  } catch (err) {
    console.error("Failed to link harmonic_person source", err)
    throw err
  }

  return { status: "found", recordId: target.id, urn, data: hit }
}

/** Link a source to a record through core, storing an optional cached context
 *  snapshot on the row. Rejects with core's `SourceConflictError` when another
 *  record already holds the source. */
function linkSource(
  recordId: number,
  sourceType: string,
  sourceId: string,
  apollo: unknown,
  context?: unknown
) {
  return getAuger(apollo).sources.add({
    recordId,
    sourceType,
    sourceId,
    context: (context ?? undefined) as Record<string, unknown> | undefined,
  })
}

/**
 * Resolve a company name to a Harmonic URN via typeahead, then fetch the
 * full company. Used when we don't have a domain.
 */
export async function enrichCompanyByName(name: string): Promise<HarmonicResult<HarmonicCompany>> {
  const trimmed = name.trim()
  if (!trimmed) return { found: false, status: "not_found" }
  try {
    const t = await harmonic.typeahead({ query: trimmed, search_type: "COMPANY" })
    const urn = t.results?.[0]?.entity_urn
    if (!urn) return { found: false, status: "not_found" }
    const company = await harmonic.getCompany(urn)
    return { found: true, data: company }
  } catch (err) {
    // Distinguishing transient (network/500) from genuine no-result is out of
    // scope; at least log so failures aren't swallowed silently.
    console.error("enrichCompanyByName failed:", err)
    return { found: false, status: "not_found" }
  }
}

// ============================================
// On-demand URN expand — fetch, dedup, link-or-create
// ============================================

export type ExpandUrnResult =
  | { status: "resolved" | "linked" | "created"; recordId: number }
  | { status: "not_found" | "error" }

export interface ExpandHarmonicDeps {
  harmonic: {
    getCompany: (urn: string) => Promise<HarmonicCompany>
    getPerson: (urn: string) => Promise<HarmonicPerson>
  }
  client: ApolloClient<unknown>
  createRecord: (
    input: { type: string; name: string },
    client: unknown
  ) => Promise<{ id: number; name: string; type: string; slug: string }>
  createSource: (
    recordId: number,
    sourceType: string,
    sourceId: string,
    client: unknown,
    context?: unknown
  ) => Promise<unknown>
}

export async function expandHarmonicUrn(
  opts: { urn: string; kind: "company" | "person" },
  deps: Pick<ExpandHarmonicDeps, "client"> & Partial<ExpandHarmonicDeps>
): Promise<ExpandUrnResult> {
  const { urn, kind } = opts
  const h = deps.harmonic ?? harmonic
  const apollo = deps.client
  const create =
    deps.createRecord ??
    ((input: { type: string; name: string }, apollo: unknown) =>
      getAuger(apollo).records.create(input))
  const attach = deps.createSource ?? linkSource

  const sourceType = HARMONIC_KIND_SOURCE_TYPE[kind]
  const recordType = HARMONIC_KIND_RECORD_TYPE[kind]

  try {
    // 1. Race guard: someone may have already created/linked this URN.
    const existing = await apollo.query({
      query: fetchRecordsForSources,
      variables: { sourceType, sourceIds: [urn] },
      fetchPolicy: "network-only",
    })
    const already = existing.data?.sources?.[0]?.record
    if (already) return { status: "resolved", recordId: already.id }

    // 2. Fetch the entity from Harmonic (through the proxy).
    const company = kind === "company" ? await h.getCompany(urn) : undefined
    const person = kind === "person" ? await h.getPerson(urn) : undefined
    const payload = company ?? person
    if (!payload?.entity_urn) return { status: "not_found" }

    // 3. Dedup against an existing but un-sourced record.
    let matchId: number | undefined
    if (person) {
      const emails = person.contact?.emails ?? []
      const found = await getAuger(apollo).records.findByEmails(emails)
      // The first of Harmonic's emails, in its order, that a person holds.
      matchId = emails.map((email) => found.get(email.trim().toLowerCase())).find(Boolean)?.id
    } else if (company) {
      const w = company.website
      const rawSite = typeof w === "string" ? w : (w?.domain ?? w?.url)
      const domain = normalizeDomain(rawSite)
      if (domain) {
        const r = await apollo.query({
          query: findRecordsByDomain,
          variables: { pattern: `%${domain}%` },
          fetchPolicy: "network-only",
        })
        // Coarse _ilike match confirmed exact in JS (avoids acme.com ~ notacme.com).
        const hit = (r.data?.record_contacts ?? []).find(
          (c) => normalizeDomain(c.value) === domain
        )?.record
        if (hit) matchId = hit.id
      }
    }

    // 4. Link branch: attach source to the existing record.
    if (matchId != null) {
      await attach(matchId, sourceType, urn, apollo, buildHarmonicContext(payload))
      return { status: "linked", recordId: matchId }
    }

    // 5. Create branch: new record + source. The owner pair is set automatically by the Hasura insert presets (x-hasura-person-id/org-id in public_records.yaml), so the record is owned by the authenticated person.
    const name = person?.full_name || company?.name || urn
    const created = await create({ type: recordType, name }, apollo)
    try {
      await attach(created.id, sourceType, urn, apollo, buildHarmonicContext(payload))
    } catch (attachErr) {
      console.error(
        "expandHarmonicUrn: source attach failed after record create",
        { urn, kind },
        attachErr
      )
    }
    return { status: "created", recordId: created.id }
  } catch (err) {
    console.error("expandHarmonicUrn failed", { urn, kind }, err)
    return { status: "error" }
  }
}
